// Etiquetas visibles de una observación postventa — fuente única para el
// dashboard (apps/web-dashboard) y la PWA (apps/web-svelte): estado,
// criticidad, especialidad y el modificador «Reabierta ×N».
//
// Por qué vive acá y no en cada cliente: las dos apps pintaban el mismo
// hecho con palabras distintas (dos nombres para `abierta`, «Leve» vs
// «Menor», la clave cruda de la categoría como especialidad). Un solo
// módulo, consumido por ambas, es la única forma de que no se separen.
//
// Reglas del módulo:
// - Exporta TONOS semánticos (`PillTone`), nunca clases CSS. Cada cliente
//   mapea tono → clase en su propio `pills.ts`.
// - Del engine importa TIPOS y, como únicas excepciones, la proyección de
//   fases (`observationPhaseKeys` / `observationEntryPhase`) que
//   `estadoLabelsFor` necesita para leer la etiqueta de cada estado de la
//   receta, y cómo se dice el rango de horas de la visita
//   (`visitRangeLabel`, F8: el vocabulario vive en el engine). No importa @bitacorapro/recipes: la especialidad y las
//   etiquetas por receta reciben la receta por argumento.
// - Se ramifica por el código estable (`ObservacionEstadoKind`,
//   `DerivedSeverity`), nunca por el texto. Las claves persistidas
//   (abierta, aceptada, …, ok/observacion/pendiente/critico, S1..S4) no
//   aparecen como literales de estado: el estado se lee de la FASE que
//   deriva el engine (fitness: observation-phase-single-source).
// - Los labels de estado y de criticidad coinciden con los de la receta
//   broker_postventa; observation-labels.test.ts fija esa paridad.
// - Desde ADR-0134 (facility adopta el hilo) la etiqueta de estado es POR
//   RECETA: `estadoLabelsFor(receta)` lee el `label` del estado que ocupa
//   cada fase, así facility pinta «Abierta» / «Resuelta» / «Anulada»
//   donde postventa pinta «Solicitada» / «Lista para revisar» /
//   «Rechazada». `ESTADO_LABEL` sigue siendo la tabla de postventa y el
//   respaldo cuando no hay receta; el test de paridad fija que derivarla
//   de broker_postventa da exactamente esa tabla.
import { appealedObservationKeys, observationEntryPhase, observationPhaseKeys, OBSERVATION_VISIT_SLOT_START_WINDOWS, visitRangeLabel, visitTimeLabel, } from "@bitacorapro/core-engine";
export const ESTADO_LABEL = {
    open: "Solicitada",
    reopened: "Solicitada",
    accepted: "Aceptada",
    assigned: "Asignada",
    resolved: "Lista para revisar",
    conforme: "Cerrada",
    closed_no_response: "Cerrada sin respuesta",
    rejected: "Rechazada",
    appealed: "Rechazo apelado",
    rejected_closed: "Rechazada cerrada",
};
export const ESTADO_TONE = {
    open: "neutral",
    reopened: "neutral",
    accepted: "info",
    assigned: "info",
    resolved: "info",
    conforme: "ok",
    // No es un cierre confirmado: tono neutro, nunca el verde de la
    // conformidad (ADR-0142 §3).
    closed_no_response: "neutral",
    rejected: "sev-high",
    appealed: "sev-med",
    rejected_closed: "neutral",
};
/**
 * ADR-0141 / F5a — la etiqueta PARA EL PROPIETARIO (tabla §4 del handoff
 * del canal único). El personal sigue leyendo los nombres internos de
 * `ESTADO_LABEL` / `estadoLabelsFor`; esta tabla es la voz del cliente y
 * no toca aquéllas. `rejected` y `rejected_closed` comparten palabra a
 * propósito: para el propietario el hecho es el mismo («no la cubre la
 * garantía»), lo que cambia es si todavía puede responder.
 */
export const ESTADO_LABEL_PROPIETARIO = {
    open: "En revisión",
    reopened: "Reabierta",
    accepted: "Coordinando visita",
    // F8 (corte 6): sin fecha agendada, la asignación sigue coordinando la
    // visita; con fecha vigente la franja dice `ESTADO_VISITA_AGENDADA`, o
    // `ESTADO_VISITA_POR_CONFIRMAR` si la propuso el contratista
    // (`estadoLabelPropietario`). Antes decía «Visita agendada» siempre y
    // prometía una visita que no existía.
    assigned: "Coordinando visita",
    resolved: "Lista para tu revisión",
    conforme: "Resuelta",
    closed_no_response: "Cerrada por falta de respuesta",
    rejected: "No cubierta por la garantía",
    appealed: "Segunda revisión",
    rejected_closed: "No cubierta por la garantía",
};
/** Siguiente paso que ve el PROPIETARIO bajo el estado de su observación
 *  (franja de estado del home, handoff propietario_home v2). Sólo lo que
 *  espera de otro o lo que le toca hacer; lo cerrado (`conforme`,
 *  `rejected_closed`) no tiene siguiente paso — su texto de apoyo con
 *  fecha sale de `apoyoPropietario`. open y reopened comparten texto,
 *  como comparten etiqueta. Copy en tú neutro, nunca voseo. */
export const SIGUIENTE_PASO_PROPIETARIO = {
    open: "Esperando revisión de la inmobiliaria",
    reopened: "Esperando revisión de la inmobiliaria",
    accepted: "Esperando asignación de contratista",
    assigned: "El contratista te contactará para la visita",
    resolved: "Confirma si quedó conforme",
    rejected: "Revisa el motivo y cuéntanos si estás de acuerdo",
    appealed: "Esperando la segunda revisión de la inmobiliaria",
};
/**
 * ADR-0140 §4 — el plazo de primera respuesta que la UI promete al
 * propietario: 10 días CORRIDOS (sin calendario de feriados), contados
 * desde que el estado actual se fijó. Es «primera respuesta», nunca
 * resolución. En una consulta (ADR-0155) vale por ronda: si el
 * propietario escribe en una consulta ya respondida, el plazo se cuenta
 * de nuevo desde su mensaje.
 */
export const PLAZO_RESPUESTA_PROPIETARIO_DIAS = 10;
const FECHA_APOYO = new Intl.DateTimeFormat("es-CL", {
    day: "numeric",
    month: "long",
    timeZone: "America/Santiago",
});
/** «2026-10-14» — el día calendario de un instante, visto desde Chile. */
const DIA_SANTIAGO = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Santiago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
});
/** «24 de octubre» — la fecha de los textos de apoyo del propietario. */
export function fechaApoyoPropietario(iso) {
    return FECHA_APOYO.format(typeof iso === "string" ? new Date(iso) : iso);
}
/**
 * La fecha comprometida de respuesta: `desde` + el plazo de ADR-0140, en
 * DÍAS DE CALENDARIO de America/Santiago — no en milisegundos: sumar
 * 10×24 h cruza el cambio de hora chileno con una hora de menos (o de
 * más) y, con un `desde` al borde de la medianoche, corre la fecha
 * prometida un día. El instante devuelto es el mediodía UTC del día
 * comprometido (siempre la mañana en Santiago, UTC-3/-4), pensado para
 * formatearse con `fechaApoyoPropietario`, no como hora de vencimiento.
 */
export function fechaCompromisoRespuesta(desdeIso) {
    const [y, m, d] = DIA_SANTIAGO.format(new Date(desdeIso)).split("-").map(Number);
    // Date.UTC normaliza el desborde de mes y de año.
    return new Date(Date.UTC(y, m - 1, d + PLAZO_RESPUESTA_PROPIETARIO_DIAS, 12));
}
/**
 * «Parque Mirador Norte · Unidad B-702» — cómo se nombra una unidad del
 * propietario. Fuente única para la PWA (título de la hoja «Consulta
 * sobre …», subtítulo de garantías) y los correos del canal del
 * propietario (consulta, acuse, asignada), así el propietario y la
 * inmobiliaria ven el mismo nombre. Null cuando no hay ni proyecto ni
 * unidad: cada llamador decide su respaldo (la dirección, «tu propiedad»).
 */
export function propiedadLabel(proyecto, unidad) {
    const p = proyecto?.trim() || null;
    const u = unidad?.trim() || null;
    return [p, u ? `Unidad ${u}` : null].filter(Boolean).join(" · ") || null;
}
/** Los kinds cuyo texto de apoyo promete una fecha de respuesta
 *  (`fechaCompromisoRespuesta`). */
const KINDS_CON_PLAZO = new Set([
    "open",
    "reopened",
    "appealed",
    "accepted",
]);
/**
 * ¿Ya pasó el día comprometido de respuesta? Compara DÍAS DE CALENDARIO de
 * America/Santiago: el mismo día del vencimiento todavía está en plazo
 * («antes del 24 de octubre» se cumple el 24); el día siguiente, vencido.
 * `hoy` es inyectable para los tests; por defecto, el reloj del cliente.
 */
export function plazoRespuestaVencido(statusAt, hoy = new Date()) {
    // El compromiso es el mediodía UTC de su día: formateado en Santiago
    // (UTC-3/-4) cae en el mismo día calendario.
    const comprometido = DIA_SANTIAGO.format(fechaCompromisoRespuesta(statusAt));
    // «AAAA-MM-DD» ordena lexicográficamente igual que cronológicamente.
    return DIA_SANTIAGO.format(hoy) > comprometido;
}
/** La etiqueta del estado del propietario cuando la visita tiene fecha. */
export const ESTADO_VISITA_AGENDADA = "Visita agendada";
/** ADR-0160 (founder 2026-10-17) — la etiqueta del estado mientras la
 *  visita que propuso el contratista sigue por confirmar
 *  (`confirmation: "pending"`). Con visita del personal, o ya
 *  confirmada, `ESTADO_VISITA_AGENDADA`. */
export const ESTADO_VISITA_POR_CONFIRMAR = "Visita por confirmar";
const DIAS_SEMANA = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
const MESES_VISITA = [
    "ene",
    "feb",
    "mar",
    "abr",
    "may",
    "jun",
    "jul",
    "ago",
    "sept",
    "oct",
    "nov",
    "dic",
];
/** «jueves 16-oct» desde un día de calendario «AAAA-MM-DD» (es el día de
 *  Santiago tal cual: no se proyecta zona). Malformado → el texto crudo. */
export function visitaDiaLabel(date) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    if (!m)
        return date;
    const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
    const dow = new Date(Date.UTC(y, mo - 1, d)).getUTCDay();
    return `${DIAS_SEMANA[dow]} ${String(d).padStart(2, "0")}-${MESES_VISITA[mo - 1] ?? ""}`;
}
/** «18-ago» — el día corto de una visita, sin el día de la semana (la
 *  encuesta de la visita, ADR-0153, como el prototipo de Mario). */
export function visitaDiaCortoLabel(date) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    if (!m)
        return date;
    return `${m[3]}-${MESES_VISITA[Number(m[2]) - 1] ?? ""}`;
}
/** «jueves 16-oct, 9:00 a 13:00» — día y rango de horas (de Santiago,
 *  tal cual: son horas de pared), en minúscula (va dentro de una frase). */
export function visitaLabel(v) {
    return `${visitaDiaLabel(v.date)}, ${visitRangeLabel(v.startTime, v.endTime)}`;
}
/** ¿La visita sigue por delante (hoy o después, en Santiago)? Una fecha
 *  pasada no se le muestra al propietario como promesa (decisión del
 *  fundador 2026-10-15). `hoy` es inyectable para los tests. */
export function visitaVigente(v, hoy = new Date()) {
    return v != null && v.date >= DIA_SANTIAGO.format(hoy);
}
/** El texto de apoyo con fecha: «Jueves 16-oct, 9:00 a 13:00 ·
 *  Terminaciones Andes». El mismo formato va en el correo y en la campana. Sólo el nombre de la EMPRESA, nunca un técnico.
 *  Una visita por confirmar (ADR-0160) lleva el mismo texto: lo que falta
 *  lo dice la etiqueta del estado («Visita por confirmar») o el título de
 *  la campana («Visita propuesta»), y repetirlo acá sería decirlo dos
 *  veces en la misma franja. */
export function apoyoVisitaAgendada(v) {
    const frase = visitaLabel(v);
    const capital = frase.charAt(0).toLocaleUpperCase("es-CL") + frase.slice(1);
    return v.providerName ? `${capital} · ${v.providerName}` : capital;
}
/** La etiqueta del estado para el propietario, con la visita: la
 *  asignación con fecha vigente dice «Visita agendada», o «Visita por
 *  confirmar» si la propuso el contratista y nadie la confirmó (ADR-0160:
 *  lo que el propietario lee no puede mentir; sin botones, confirmar es
 *  la próxima ola); sin fecha, la de `ESTADO_LABEL_PROPIETARIO`. `hoy` es
 *  inyectable para los tests. */
export function estadoLabelPropietario(kind, visit, hoy = new Date()) {
    if (kind === "assigned" && visit != null && visitaEnPie(visit, hoy)) {
        return visit.confirmation === "pending" ? ESTADO_VISITA_POR_CONFIRMAR : ESTADO_VISITA_AGENDADA;
    }
    return ESTADO_LABEL_PROPIETARIO[kind];
}
/** ADR-0142 — el texto de apoyo del propietario sobre una observación
 *  «Lista para tu revisión» cuando el cierre por falta de respuesta está
 *  encendido. `fecha` = `autoApprovalAt` del wire. */
export function apoyoEsperaCierre(autoApprovalAt) {
    return `Confírmanos si quedó bien antes del ${fechaApoyoPropietario(autoApprovalAt)}`;
}
/** ADR-0142 — la regla bajo «Revisar y confirmar» (home y hilo del
 *  propietario): qué pasa si no responde. Sólo con `autoApprovalAt` del
 *  wire; sin él la observación no se cierra sola y el cliente lo dice. */
export function avisoCierrePrevisto(autoApprovalAt) {
    return `Si no respondes, se cerrará el ${fechaApoyoPropietario(autoApprovalAt)}.`;
}
/** ADR-0142 — la fecha de cierre previsto para el personal (panel del
 *  jefe): «Se cierra el 5 de noviembre si el propietario no responde». */
export function cierrePrevistoPersonal(autoApprovalAt) {
    return `Se cierra el ${fechaApoyoPropietario(autoApprovalAt)} si el propietario no responde`;
}
/** ADR-0142 §3 — el texto de apoyo de una observación cerrada por falta
 *  de respuesta. Con la ventana de reapertura vigente nombra su último
 *  día (`reopenableUntil` del wire); vencida, sólo el hecho. */
export function apoyoCerradaSinRespuesta(reopenableUntil, hoy = new Date()) {
    if (reopenableUntil != null) {
        const until = typeof reopenableUntil === "string" ? new Date(reopenableUntil) : reopenableUntil;
        if (hoy.getTime() <= until.getTime()) {
            return `No recibimos tu respuesta. Puedes reabrirla hasta el ${fechaApoyoPropietario(until)}`;
        }
    }
    return "No recibimos tu respuesta.";
}
/**
 * F5a — el texto de apoyo CON FECHA bajo el estado del propietario
 * (tabla §4 del handoff). Null cuando el kind no promete fecha (la
 * asignación sólo la promete con una visita agendada vigente, F8; «Lista para tu revisión» sólo
 * promete fecha cuando el servidor proyecta `autoApprovalAt`, ADR-0142),
 * cuando falta la fecha de la que se calcula, o cuando la fecha
 * prometida YA PASÓ (decisión del fundador 2026-10-15: nunca mostrar una
 * promesa vencida como si siguiera vigente). Días corridos, sin feriados
 * (ADR-0140 §4). `hoy` es inyectable para los tests.
 */
export function apoyoPropietario(kind, fechas, hoy = new Date()) {
    const statusAt = fechas.statusAt ?? null;
    if (statusAt && KINDS_CON_PLAZO.has(kind) && plazoRespuestaVencido(statusAt, hoy)) {
        return null;
    }
    switch (kind) {
        case "open":
        case "reopened":
        case "appealed":
            return statusAt
                ? `Te responderemos antes del ${fechaApoyoPropietario(fechaCompromisoRespuesta(statusAt))}`
                : null;
        case "accepted":
            return statusAt
                ? `Te confirmaremos la fecha antes del ${fechaApoyoPropietario(fechaCompromisoRespuesta(statusAt))}`
                : null;
        case "assigned":
            // F8 — con fecha vigente, el día, el rango de horas y la empresa; sin ella,
            // el siguiente paso fijo («El contratista te contactará…»).
            return fechas.visit && visitaEnPie(fechas.visit, hoy)
                ? apoyoVisitaAgendada(fechas.visit)
                : null;
        case "rejected_closed":
            return statusAt ? `Cerrada el ${fechaApoyoPropietario(statusAt)}` : null;
        case "conforme": {
            const closedAt = fechas.closedAt ?? null;
            return closedAt ? `Confirmaste el arreglo el ${fechaApoyoPropietario(closedAt)}` : null;
        }
        case "resolved": {
            const at = fechas.autoApprovalAt ?? null;
            // Una fecha ya pasada no se promete (el job cierra en el tick de
            // ese día; si un aviso falló, el servidor ya corrió la fecha).
            if (at == null)
                return null;
            return DIA_SANTIAGO.format(hoy) > DIA_SANTIAGO.format(new Date(at))
                ? null
                : apoyoEsperaCierre(at);
        }
        case "closed_no_response":
            return apoyoCerradaSinRespuesta(fechas.reopenableUntil, hoy);
        default:
            return null;
    }
}
/** El siguiente paso del propietario, con fecha cuando el kind la
 *  promete (`apoyoPropietario`) y el texto fijo si no. Con el plazo
 *  vencido no hay siguiente paso: la franja dice sólo el estado («En
 *  revisión», «Coordinando visita», «Segunda revisión»), sin fecha ni
 *  texto de espera. `hoy` es inyectable para los tests. */
export function siguientePasoPropietario(kind, fechas, hoy = new Date()) {
    const statusAt = fechas.statusAt ?? null;
    if (statusAt && KINDS_CON_PLAZO.has(kind) && plazoRespuestaVencido(statusAt, hoy)) {
        return undefined;
    }
    return apoyoPropietario(kind, fechas, hoy) ?? SIGUIENTE_PASO_PROPIETARIO[kind];
}
/** Escala de criticidad de 4 niveles (fundador 2026-10-02), la misma en
 *  toda superficie. */
export const CRITICIDAD_LABEL = {
    S1: "Crítica",
    S2: "Mayor",
    S3: "Menor",
    S4: "Estética",
};
export const CRITICIDAD_TONE = {
    S1: "sev-peak",
    S2: "sev-high",
    S3: "sev-med",
    S4: "neutral",
};
/** Tono del modificador «Reabierta ×N». */
export const REABIERTA_TONE = "sev-high";
/** Badge legado (clave de severidad de la receta) → S-code. Mismo mapa
 *  que `legacyDisplayAs` de broker_postventa; es el respaldo del cliente
 *  cuando el wire no trae `effectiveSeverity`. */
export const LEGACY_SEVERITY_TO_DERIVED = {
    critico: "S1",
    pendiente: "S2",
    observacion: "S3",
    ok: "S4",
};
/**
 * Qué ES la fila, desde la fase que deriva el engine (`observationPhase`)
 * más el overlay de cierre. Misma lógica que tenía el dashboard en
 * `lib/postventa/phase.ts`:
 *
 * - triage (o fase desconocida/null) → `appealed` si el engine marcó la
 *   apelación (ADR-0141, modificador derivado), si no `reopened` con al
 *   menos una reapertura, si no `open`;
 * - awaiting_assignment → `accepted`, salvo que el engine la marque
 *   `reopened`: eso sólo pasa en un grafo SIN triage (facility,
 *   ADR-0134), donde la No conforme vuelve a «aceptada», que es la
 *   entrada. En postventa `reopened` nunca es verdadero fuera de triage,
 *   así que una reabierta ya re-aceptada sigue leyendo `accepted`;
 * - awaiting_receiver_on_rejection → `rejected`: rechazo comunicado, a la
 *   espera de la respuesta del propietario (ADR-0141);
 * - closed → `conforme` si el overlay de cierre está estampado (motivo o
 *   fecha: lo cerró el receptor). Sin overlay, el cierre lo puso un nodo
 *   terminal del grafo y el kind depende de la FORMA de la receta
 *   (`rejectionAwaitsReceiver`): en un grafo CON carril de espera tras el
 *   rechazo (postventa) ese nodo es el cierre del carril →
 *   `rejected_closed` (rechazada_cerrada, o una rechazada legacy cerrada
 *   por la migración de ADR-0141); en un grafo SIN ese carril (facility)
 *   el nodo terminal ES el rechazo → `rejected`, exactamente como antes
 *   de ADR-0141 (el pill de la anulada no cambia de tono).
 */
export function estadoKindFor(i) {
    switch (i.phase) {
        case "awaiting_assignment":
            return i.reopened === true ? "reopened" : "accepted";
        case "with_assignee":
            return "assigned";
        case "awaiting_receiver":
            return "resolved";
        case "awaiting_receiver_on_rejection":
            return "rejected";
        case "closed":
            if (i.closedReason === "auto_approval")
                return "closed_no_response";
            if (i.closedReason != null || i.closedAt != null)
                return "conforme";
            return i.rejectionAwaitsReceiver === false ? "rejected" : "rejected_closed";
        case "triage":
        case null:
        default:
            if (i.appealed === true)
                return "appealed";
            return i.reopenCount > 0 ? "reopened" : "open";
    }
}
const REJECTION_WAIT_CACHE = new WeakMap();
/**
 * Si el grafo de la receta tiene el carril de espera tras el rechazo
 * (fase `awaiting_receiver_on_rejection`, ADR-0141) — el dato de receta
 * que `estadoKindFor` usa para separar el rechazo terminal directo
 * (facility: anulada, kind `rejected`) del cierre que llega desde la
 * espera (postventa: rechazada_cerrada, kind `rejected_closed`). Nunca
 * por slug ni por clave de estado. `null` sin receta: el llamador no
 * puede saberlo y `estadoKindFor` cae a la forma de postventa. Cacheado
 * por referencia de vocabulario, como `estadoLabelsFor`.
 */
export function rejectionAwaitsReceiverFor(vocab) {
    if (!vocab?.observationStatuses || vocab.observationStatuses.length === 0)
        return null;
    let v = REJECTION_WAIT_CACHE.get(vocab);
    if (v === undefined) {
        v = observationPhaseKeys(vocab).awaiting_receiver_on_rejection.length > 0;
        REJECTION_WAIT_CACHE.set(vocab, v);
    }
    return v;
}
/** El veredicto del carril de cierre sobre lo resuelto (POST
 *  /findings/:id/conformity): el texto de sus dos botones, igual en toda
 *  superficie que lo ofrezca (PWA del propietario, PWA del jefe de tienda,
 *  panel del supervisor de facility, ADR-0134). */
export const VEREDICTO_LABEL = {
    conforme: "Conforme",
    no_conforme: "No conforme",
};
/**
 * La etiqueta de cada kind según la receta: el `label` del primer estado
 * vivo de la fase que ese kind representa (`observationPhaseKeys`).
 * open / reopened leen la fase de ENTRADA (triage si el grafo la tiene,
 * si no la espera de asignación: facility, ADR-0134). `conforme` no es un
 * estado sino el overlay de cierre, así que no sale de la receta y queda
 * en «Cerrada» para toda vertical; `closed_no_response` tampoco
 * («Cerrada por falta de respuesta», ADR-0142).
 */
export function estadoLabelsFor(vocab) {
    if (vocab.observationStatuses.length === 0)
        return {};
    const keys = observationPhaseKeys(vocab);
    const labelOf = (phase) => {
        const key = keys[phase][0];
        return key === undefined
            ? undefined
            : vocab.observationStatuses.find((s) => s.key === key)?.label;
    };
    const entry = labelOf(observationEntryPhase(keys));
    const out = {};
    const put = (kind, label) => {
        if (label)
            out[kind] = label;
    };
    put("open", entry);
    put("reopened", entry);
    put("accepted", labelOf("awaiting_assignment"));
    put("assigned", labelOf("with_assignee"));
    put("resolved", labelOf("awaiting_receiver"));
    // ADR-0141: `rejected` es la espera de respuesta del propietario; el
    // cierre del grafo (rechazada_cerrada de postventa) es
    // `rejected_closed`. En un grafo SIN carril de espera (facility) el
    // rechazo ES el nodo terminal: su kind sigue siendo `rejected`
    // (estadoKindFor con `rejectionAwaitsReceiver: false`) y su etiqueta
    // sale del cierre («Anulada»). La apelación lee la etiqueta del primer
    // estado apelado (postventa: «Rechazo apelado»).
    put("rejected", labelOf("awaiting_receiver_on_rejection") ?? labelOf("closed"));
    put("rejected_closed", labelOf("closed"));
    const appealedKey = appealedObservationKeys(vocab)[0];
    put("appealed", appealedKey === undefined
        ? undefined
        : vocab.observationStatuses.find((s) => s.key === appealedKey)?.label);
    return out;
}
export function estadoFor(kind, labels) {
    return { label: labels?.[kind] ?? ESTADO_LABEL[kind], tone: ESTADO_TONE[kind] };
}
function isDerivedSeverity(v) {
    return typeof v === "string" && Object.hasOwn(CRITICIDAD_LABEL, v);
}
/**
 * El pill de criticidad: la S-code efectiva si viene, si no la clave de
 * severidad legado traducida por `LEGACY_SEVERITY_TO_DERIVED`. `null`
 * cuando no hay de dónde leer — el cliente no pinta el pill.
 */
export function criticidadFor(code, legacy) {
    let resolved = isDerivedSeverity(code) ? code : null;
    if (resolved === null && legacy != null && Object.hasOwn(LEGACY_SEVERITY_TO_DERIVED, legacy)) {
        resolved = LEGACY_SEVERITY_TO_DERIVED[legacy];
    }
    if (resolved === null)
        return null;
    return { code: resolved, label: CRITICIDAD_LABEL[resolved], tone: CRITICIDAD_TONE[resolved] };
}
/** «Reabierta ×N». N = reaperturas = `reopenCount` del wire. */
export function reabiertaLabel(n) {
    return `Reabierta ×${n}`;
}
/**
 * Especialidad = etiqueta de `recipe.findingCategories` para la
 * `category` de la observación. Clave desconocida → la etiqueta de
 * `otro` si la receta la declara; si no, `null`. Nunca la clave cruda.
 */
export function especialidadLabel(categories, key) {
    const hit = key != null ? categories.find((c) => c.key === key) : undefined;
    return hit?.label ?? categories.find((c) => c.key === "otro")?.label ?? null;
}
/** Motivo de «Requiere nueva visita» (ADR-0123): lo eligen los chips del
 *  contratista, lo pinta el aviso de su tarjeta y lo lleva el correo al
 *  jefe de postventa. Una sola fuente para PWA y auth-svc; el wire viaja
 *  con la clave del enum, nunca con el texto. */
export const PUBLIC_VISIT_REASON_LABEL = {
    sin_acceso: "Sin acceso a la unidad",
    falta_material: "Falta material",
    requiere_otro_especialista: "Requiere otro especialista",
    trabajo_mayor: "Trabajo mayor al previsto",
};
/** La etiqueta de un motivo de «nueva visita» con el sustantivo del
 *  activo de la vertical: «Sin acceso a la tienda». El resto de los
 *  motivos no nombra el activo y sale tal cual de
 *  `PUBLIC_VISIT_REASON_LABEL`. Los sustantivos de activo de las
 *  verticales con portal son femeninos (Unidad / Tienda): una vertical
 *  con uno masculino tiene que revisar esta frase. Con «Unidad» da
 *  exactamente `PUBLIC_VISIT_REASON_LABEL`. */
export function visitReasonLabelForAsset(reason, assetNoun) {
    if (reason === "sin_acceso")
        return `Sin acceso a la ${assetNoun.toLocaleLowerCase("es-CL")}`;
    return PUBLIC_VISIT_REASON_LABEL[reason];
}
// ─── ADR-0160 (fase 3, ola 2) — el contratista agenda desde su enlace ──
/** Para el contratista y el jefe: la visita que propuso el contratista
 *  sigue esperando al propietario. */
export const VISITA_POR_CONFIRMAR = "Por confirmar con el propietario";
const HORA_SANTIAGO = new Intl.DateTimeFormat("en-GB", {
    timeZone: "America/Santiago",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
});
/** «El contratista, el 17-oct a las 09:30» — quién y cuándo la propuso,
 *  en hora de Santiago (fila «Agendó» del panel del jefe: la clave ya
 *  dice el verbo, y «el … a las» la separa del día de la visita). */
export function propuestaPorContratistaLabel(at) {
    const instante = typeof at === "string" ? new Date(at) : at;
    const dia = visitaDiaCortoLabel(DIA_SANTIAGO.format(instante));
    return `El contratista, el ${dia} a las ${HORA_SANTIAGO.format(instante)}`;
}
// ─── ADR-0161 (fase 3, ola 3, #0768) — el propietario confirma la visita ──
/** ¿La visita sigue en pie para el propietario? Vigente y no rechazada:
 *  la fecha que el propietario pidió cambiar no se le muestra como
 *  promesa (el wire del propietario ya la manda como null; esto cubre la
 *  campana y cualquier lector que reciba la fila cruda). */
export function visitaEnPie(v, hoy = new Date()) {
    return visitaVigente(v, hoy) && v?.confirmation !== "other_time_requested";
}
/** «sábado 19-oct a las 14:30» — un instante, en hora de Santiago. */
export function fechaHoraSantiagoLabel(at) {
    const instante = typeof at === "string" ? new Date(at) : at;
    return `${visitaDiaLabel(DIA_SANTIAGO.format(instante))} a las ${visitTimeLabel(HORA_SANTIAGO.format(instante))}`;
}
/** La regla bajo «Confirmar visita»: qué pasa si no responde. Sólo con
 *  `confirmBy` del wire; sin él la visita no se confirma sola. */
export function avisoConfirmacionPrevista(confirmBy) {
    return `Si no respondes antes del ${fechaHoraSantiagoLabel(confirmBy)}, la daremos por confirmada.`;
}
/** Bajo «Confirmar visita» cuando no hay hora límite. */
export const AVISO_RESPONDE_ANTES_DE_LA_VISITA = "Responde antes de la visita.";
/** La hora límite ya pasó y el tick todavía no corrió. */
export const AVISO_VISITA_CONFIRMANDOSE = "El plazo para responder terminó; la visita quedará confirmada en unos minutos.";
/** Después de pedir otro horario (neutro: puede proponer el contratista o
 *  el jefe). No promete un aviso: el del jefe es opcional y el propietario
 *  pudo haberlo apagado; la tarjeta y el hilo sí lo muestran siempre. */
export const APOYO_OTRO_HORARIO_PEDIDO = "Pediste otro horario. Cuando haya un nuevo horario, lo verás aquí.";
/** Unidad arrendada (ADR-0150): quien está en la vivienda no recibe el
 *  aviso, el propietario sí. */
export const AVISO_AVISAR_OCUPANTE = "Recuerda avisar a quien esté en la vivienda.";
function franjaLabel(slot) {
    const w = OBSERVATION_VISIT_SLOT_START_WINDOWS[slot];
    return `${visitTimeLabel(w.from)} a ${visitTimeLabel(w.to)}`;
}
/** La franja dentro de una frase, armada desde las constantes del engine. */
export const VISIT_SLOT_LABEL = {
    morning: `en la mañana (${franjaLabel("morning")})`,
    afternoon: `en la tarde (${franjaLabel("afternoon")})`,
    any: "a cualquier hora",
};
/** El rótulo de cada opción del control de la hoja. */
export const VISIT_SLOT_OPTION_LABEL = {
    morning: "Mañana",
    afternoon: "Tarde",
    any: "Cualquiera",
};
/** El detalle de cada opción (bajo el rótulo): «8:00 a 13:00». */
export const VISIT_SLOT_OPTION_HINT = {
    morning: franjaLabel("morning"),
    afternoon: franjaLabel("afternoon"),
    any: null,
};
/** «lunes 20-oct, martes 21-oct · en la tarde (13:00 a 19:00)». */
export function preferenciaHorarioLabel(dates, slot) {
    const dias = [...dates].sort().map(visitaDiaLabel).join(", ");
    return `${dias} · ${VISIT_SLOT_LABEL[slot]}`;
}
/** «Pediste otro horario: …. Cuando haya un nuevo horario, lo verás aquí.» */
export function apoyoOtroHorarioPedido(pref) {
    if (pref == null)
        return APOYO_OTRO_HORARIO_PEDIDO;
    return `Pediste otro horario: ${preferenciaHorarioLabel(pref.preferredDates, pref.preferredSlot)}. Cuando haya un nuevo horario, lo verás aquí.`;
}
/** El estado de la confirmación, para el panel del jefe. */
export function visitaConfirmacionLabel(confirmation) {
    switch (confirmation) {
        case "pending":
            return "Por confirmar";
        case "confirmed":
            return "Confirmada por el propietario";
        case "auto_confirmed":
            return "Confirmada sin respuesta del propietario";
        case "other_time_requested":
            return "El propietario pidió otro horario";
        default:
            return null;
    }
}
/** 422 `VISIT_TOO_SOON` y su validación en el cliente. */
export const VISIT_TOO_SOON_MESSAGE = "La visita tiene que empezar al menos 24 horas después de ahora, para que el propietario alcance a confirmarla.";
/** 409 `VISIT_SAME_AS_DECLINED` y su validación en el cliente. */
export const VISIT_SAME_AS_DECLINED_MESSAGE = "Es el horario que el propietario pidió cambiar. Elige otro día u horario.";
/** Aviso que no bloquea: la preferencia es orientativa. */
export const VISIT_PREFERENCE_MISMATCH_HINT = "No calza con los días u horario que pidió el propietario.";
/** El rótulo de la visita rechazada para el contratista: «Horario
 *  rechazado: jueves 16-oct, 9:00 a 13:00». */
export function horarioRechazadoLabel(v) {
    return `Horario rechazado: ${visitaLabel(v)}`;
}
/** «El propietario prefiere: …» para el contratista. */
export function propietarioPrefiereLabel(dates, slot) {
    return `El propietario prefiere: ${preferenciaHorarioLabel(dates, slot)}`;
}
/** Para el contratista: la visita según la respuesta del propietario. */
export const VISITA_CONFIRMADA_POR_PROPIETARIO = "Confirmada por el propietario";
export const VISITA_CONFIRMADA_SIN_RESPUESTA = "Confirmada (el propietario no respondió a tiempo)";
// ─── ADR-0163 (fase 3, ola 3) — la consulta convertida en observación ──
/** Las tres preguntas de severidad que el jefe responde al convertir (las
 *  respuestas derivan la criticidad: P1 → Crítica, P2 → Mayor, P3 → Menor,
 *  ninguna → Estética). */
export const SEVERIDAD_PREGUNTA = {
    p1Risk: "¿Hay riesgo para las personas o la vivienda?",
    p2FunctionOrProgression: "¿Impide usar algo o puede empeorar?",
    p3MinorFunctional: "¿Afecta el uso en algo menor?",
};
/** La consulta pasó a observación (franja de la consulta, bandeja, PWA).
 *  El propietario lee este texto solo: nunca ve folios (regla 9 del spec). */
export const CONSULTA_PASO_A_OBSERVACION = "Pasó a observación";
/** Sólo para el EQUIPO (dashboard): «Pasó a observación BIT-2026-0047»; sin
 *  folio, el texto solo. El carril del propietario usa
 *  `CONSULTA_PASO_A_OBSERVACION`. */
export function consultaPasoAObservacionLabel(folio) {
    return folio ? `${CONSULTA_PASO_A_OBSERVACION} ${folio}` : CONSULTA_PASO_A_OBSERVACION;
}
/** La pill de la tarjeta del propietario. */
export const DESDE_TU_CONSULTA = "Desde tu consulta";
/** El rótulo del nacimiento en el hilo del propietario autor. */
export const LO_CONTASTE_EN_UNA_CONSULTA = "Lo contaste en una consulta";
/** El rótulo de sus palabras en el hilo del propietario autor. */
export const TU_CONSULTA = "Tu consulta";
/** El origen para el equipo (detalle y filtro del panel). */
export const CONSULTA_DEL_PROPIETARIO = "Consulta del propietario";
/** El tag de un campo prellenado por la IA en el formulario del jefe. */
export const SUGERIDO_POR_LA_IA = "Sugerido por la IA";
/** La etiqueta de cada eje de la conversión (lista de faltantes del 422). */
export const CONVERSION_EJE_LABEL = {
    recinto: "Recinto",
    elemento: "Elemento",
    tipo_reparacion: "Tipo de reparación",
    tipo_falla: "Tipo de observación",
    agua_activa: "Si hay agua activa",
    severidad: "Las preguntas de criticidad",
};
//# sourceMappingURL=observation-labels.js.map